/* eslint-disable no-empty-pattern */
import React from 'react';
import type { ViewProps } from 'react-native';
import { Platform } from 'react-native';
import { useFlipper } from '@react-navigation/devtools';
import { NavigationContainer } from '@react-navigation/native';
import { datadog } from '@sg/expo-datadog-client';
import { theme } from '@sg/garnish';

import { LoadingAnimation } from '@order/components';
import { useSweetpass } from '@order/hooks';
import { useLaunchDarkly } from '@order/LaunchDarkly';
import { getEnvVars } from '@order/utils';

import { NoticeBannersOverlay } from '../components/NoticeBannersOverlay';
import { useRemoveKustomerWidget } from '../integrations/kustomer';
import * as Screens from '../screens';
import { appNavigationLinking } from './AppNavigation.linking';
import {
  useAccountTabStackOptions,
  useAppStackOptions,
  useAuthStackOptions,
  useErrorStackOptions,
  useHomeTabStackOptions,
  useLoyaltyTabStackOptions,
  useMainTabsOptions,
  useMaintenanceStackOptions,
  useMenuTabStackOptions,
  useModalStackOptions,
} from './AppNavigation.options';
import type {
  AccountTabScreenProps,
  AuthStackScreenProps,
  ErrorStackScreenProps,
  HomeTabScreenProps,
  LoyaltyTabScreenProps,
  MainTabsScreenProps,
  MenuTabScreenProps,
  ModalStackScreenProps,
} from './AppNavigation.props';
import {
  AccountTabStackNavigator,
  AppStackNavigator,
  AuthStackNavigator,
  ErrorStackNavigator,
  HomeTabStackNavigator,
  LoyaltyTabStackNavigator,
  MainTabsNavigator,
  MaintenanceStackNavigator,
  MenuTabStackNavigator,
  ModalStackNavigator,
} from './AppNavigation.props';
import { getAppNavigationContainerRef } from './AppNavigation.ref';
import { navigationDocumentTitleOptions } from './AppNavigation.utils';

const { ENABLE_FLIPPER } = getEnvVars();

export const AppNavigation = (): React.ReactElement => {
  const navigationContainerRef = getAppNavigationContainerRef();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  ENABLE_FLIPPER === 'true' && useFlipper(navigationContainerRef);
  const { loadOverrides } = useLaunchDarkly();

  const handleOnReady = React.useCallback(() => {
    loadOverrides(navigationContainerRef);

    if (Platform.OS === 'web') return; // web tracks by default
    datadog.trackViews(navigationContainerRef.current);
  }, [navigationContainerRef, loadOverrides]);

  // ─────────────────────────────────────────────────────────────────

  return (
    <NavigationContainer
      ref={navigationContainerRef}
      linking={appNavigationLinking}
      fallback={
        <LoadingAnimation
          withNavbarOffset
          backgroundColor={theme.colors.OATMEAL}
        />
      }
      onReady={handleOnReady}
      documentTitle={navigationDocumentTitleOptions}
    >
      <AppScreen />
      <NoticeBannersOverlay />
    </NavigationContainer>
  );
};

// ==================================================
// APP
// ==================================================

const AppScreen = ({}: ViewProps): React.ReactElement => {
  const stackOptions = useAppStackOptions();
  const Stack = AppStackNavigator;

  return (
    <Stack.Navigator screenOptions={stackOptions.Navigator}>
      <Stack.Screen
        name="MainTabs"
        component={MainTabsScreen}
        options={stackOptions.MainTabs}
      />
      <Stack.Screen
        name="Modal"
        component={ModalStackScreen}
        options={stackOptions.Modal}
      />
      <Stack.Screen
        name="Auth"
        component={AuthStackScreen}
        options={stackOptions.Auth}
      />
      <Stack.Screen
        name="Error"
        component={ErrorStackScreen}
        options={stackOptions.Error}
      />
      <Stack.Screen
        name="Maintenance"
        component={MaintenanceStackScreen}
        options={stackOptions.Maintenance}
      />
    </Stack.Navigator>
  );
};

// ==================================================
// MAIN TABS
// ==================================================

export const MainTabsScreen = ({}: MainTabsScreenProps): React.ReactElement => {
  useRemoveKustomerWidget();
  const tabsOptions = useMainTabsOptions();
  const Tabs = MainTabsNavigator;

  return (
    <Tabs.Navigator
      tabBar={tabsOptions.TabBar}
      screenOptions={tabsOptions.Navigator}
    >
      <Tabs.Screen
        name="HomeTab"
        component={HomeTabScreen}
        options={tabsOptions.HomeTab}
      />
      <Tabs.Screen
        name="MenuTab"
        component={MenuTabScreen}
        options={tabsOptions.MenuTab}
      />
      <Tabs.Screen
        name="LoyaltyTab"
        component={LoyaltyTabScreen}
        options={tabsOptions.LoyaltyTab}
      />
      <Tabs.Screen
        name="AccountTab"
        component={AccountTabScreen}
        options={tabsOptions.AccountTab}
      />
    </Tabs.Navigator>
  );
};

// ==================================================
// HOME TAB
// ==================================================

const HomeTabScreen = ({}: HomeTabScreenProps): React.ReactElement => {
  const stackOptions = useHomeTabStackOptions();
  const Stack = HomeTabStackNavigator;

  return (
    <Stack.Navigator screenOptions={stackOptions.Navigator}>
      <Stack.Screen
        name="Home"
        component={Screens.HomeScreen}
        options={stackOptions.Home}
      />
    </Stack.Navigator>
  );
};

// ==================================================
// MENU TAB
// ==================================================

export const MenuTabScreen = ({}: MenuTabScreenProps): React.ReactElement => {
  const stackOptions = useMenuTabStackOptions();
  const Stack = MenuTabStackNavigator;

  return (
    <Stack.Navigator screenOptions={stackOptions.Navigator}>
      <Stack.Screen
        name="Location"
        component={Screens.LocationScreen}
        options={stackOptions.Location}
      />
      {/* Note: this technically breaks the standard React Navigation NotFound fallback, such that all 404's route through the MenuScreen
      @TODO: XP-3153 improve handling of 301 / 404  */}
      <Stack.Screen
        name="MenuShort"
        component={Screens.MenuScreen}
        options={stackOptions.Menu}
      />
      <Stack.Screen
        name="Menu"
        component={Screens.MenuScreen}
        options={stackOptions.Menu}
      />
      <Stack.Screen
        name="DeliveryMenu"
        component={Screens.DeliveryMenuScreen}
        options={stackOptions.DeliveryMenu}
      />
      <Stack.Screen
        name="MenuCollection"
        component={Screens.MenuCollectionScreen}
        options={stackOptions.MenuCollection}
      />
      <Stack.Screen
        name="DeliveryMenuCollection"
        component={Screens.DeliveryMenuCollectionScreen}
        options={stackOptions.DeliveryMenuCollection}
      />
    </Stack.Navigator>
  );
};

// ==================================================
// LOYALTY TAB
// ==================================================

export const LoyaltyTabScreen =
  ({}: LoyaltyTabScreenProps): React.ReactElement => {
    const stackOptions = useLoyaltyTabStackOptions();
    const Stack = LoyaltyTabStackNavigator;
    const { isSweetpassEnabled } = useSweetpass();

    return (
      <Stack.Navigator screenOptions={stackOptions.Navigator}>
        {isSweetpassEnabled ? (
          <Stack.Screen
            name="Sweetpass"
            component={Screens.SweetpassScreen}
            options={stackOptions.Sweetpass}
          />
        ) : (
          <Stack.Screen
            name="Challenges"
            component={Screens.ChallengesScreen}
            options={stackOptions.Challenges}
          />
        )}
      </Stack.Navigator>
    );
  };

// ==================================================
// ACCOUNT TAB
// ==================================================

export const AccountTabScreen =
  ({}: AccountTabScreenProps): React.ReactElement => {
    const stackOptions = useAccountTabStackOptions();
    const Stack = AccountTabStackNavigator;

    return (
      <Stack.Navigator screenOptions={stackOptions.Navigator}>
        <Stack.Screen
          name="AccountMenu"
          component={Screens.AccountScreen}
          options={stackOptions.AccountMenu}
        />
        <Stack.Screen
          name="Profile"
          component={Screens.ProfileScreen}
          options={stackOptions.Profile}
        />
        <Stack.Screen
          name="SweetpassMembership"
          component={Screens.SweetpassMembershipScreen}
          options={stackOptions.SweetpassMembership}
        />
        <Stack.Screen
          name="PaymentCredit"
          component={Screens.PaymentCreditScreen}
          options={stackOptions.PaymentCredit}
        />
        <Stack.Screen
          name="Addresses"
          component={Screens.AddressesScreen}
          options={stackOptions.Addresses}
        />
        <Stack.Screen
          name="Orders"
          component={Screens.OrdersScreen}
          options={stackOptions.Orders}
        />
        <Stack.Screen
          name="Favorites"
          component={Screens.FavoritesScreen}
          options={stackOptions.Favorites}
        />
        <Stack.Screen
          name="ReferFriend"
          component={Screens.ReferFriendScreen}
          options={stackOptions.ReferFriend}
        />
        <Stack.Screen
          name="AccountDietaryRestrictions"
          component={Screens.AccountDietaryRestrictionsScreen}
          options={stackOptions.AccountDietaryRestrictions}
        />
      </Stack.Navigator>
    );
  };

// ==================================================
// MODAL STACK
// ==================================================

export const ModalStackScreen = (
  props: ModalStackScreenProps,
): React.ReactElement => {
  useRemoveKustomerWidget();
  const stackOptions = useModalStackOptions(props);
  const Stack = ModalStackNavigator;

  return (
    <Stack.Navigator
      screenOptions={stackOptions.Navigator}
      defaultScreenOptions={stackOptions.Default}
    >
      <Stack.Screen
        name="ProductDetails"
        component={Screens.ProductDetailsScreen}
        options={stackOptions.ProductDetails}
      />
      <Stack.Screen
        name="EditProductDetails"
        component={Screens.EditProductDetailsScreen}
        options={stackOptions.EditProductDetails}
      />
      <Stack.Screen
        name="DeliveryProductDetails"
        component={Screens.DeliveryProductDetailsScreen}
        options={stackOptions.DeliveryProductDetails}
      />
      <Stack.Screen
        name="DeliveryEditProductDetails"
        component={Screens.DeliveryEditProductDetailsScreen}
        options={stackOptions.DeliveryEditProductDetails}
      />
      <Stack.Screen
        name="ReorderConfirmLocation"
        component={Screens.ReorderConfirmLocationScreen}
        options={stackOptions.ReorderConfirmLocation}
      />
      <Stack.Screen
        name="ReorderChangeLocation"
        component={Screens.ReorderChangeLocationScreen}
        options={stackOptions.ReorderChangeLocation}
      />
      <Stack.Screen
        name="ReorderConflictReview"
        component={Screens.ReorderConflictReviewScreen}
        options={stackOptions.ReorderConflictReview}
      />
      <Stack.Screen
        name="ReorderActiveBagWarning"
        component={Screens.ReorderActiveBagWarningScreen}
        options={stackOptions.ReorderActiveBagWarning}
      />
      <Stack.Screen
        name="RateOrder"
        component={Screens.RateOrderScreen}
        options={stackOptions.RateOrder}
      />
      <Stack.Screen
        name="Bag"
        component={Screens.BagScreen}
        options={stackOptions.Bag}
      />
      <Stack.Screen
        name="Checkout"
        component={Screens.CheckoutScreen}
        options={stackOptions.Checkout}
      />
      <Stack.Screen
        name="OrderStatus"
        component={Screens.OrderStatusScreen}
        options={stackOptions.OrderStatus}
      />
      <Stack.Screen
        name="ShareLineItem"
        component={Screens.ShareLineItemScreen}
        options={stackOptions.ShareLineItem}
      />
      <Stack.Screen
        name="ScanToPay"
        component={Screens.ScanToPayOptions}
        options={stackOptions.ScanToPay}
      />
      <Stack.Screen
        name="Payments"
        component={Screens.ScanToPayPayments}
        options={stackOptions.Payments}
      />
      <Stack.Screen
        name="CreditAdd"
        component={Screens.ScanToPayGiftCode}
        options={stackOptions.CreditAdd}
      />
      <Stack.Screen
        name="CreditDetails"
        component={Screens.CreditDetailsScreen}
        options={stackOptions.CreditDetails}
      />
      <Stack.Screen
        name="PaymentCredit"
        component={Screens.PaymentCreditScreen}
        options={stackOptions.PaymentCredit}
      />
      <Stack.Screen
        name="PaymentMethodAdd"
        component={Screens.PaymentMethodAddScreen}
        options={stackOptions.PaymentMethodAdd}
      />
      <Stack.Screen
        name="PaymentMethodEdit"
        component={Screens.PaymentMethodEditScreen}
        options={stackOptions.PaymentMethodEdit}
      />
      <Stack.Screen
        name="AddressAdd"
        component={Screens.AddressAddScreen}
        options={stackOptions.AddressAdd}
      />
      <Stack.Screen
        name="AddressEdit"
        component={Screens.AddressEditScreen}
        options={stackOptions.AddressEdit}
      />
      <Stack.Screen
        name="DietaryRestrictions"
        component={Screens.DietaryRestrictionsScreen}
        options={stackOptions.DietaryRestrictions}
      />
      <Stack.Screen
        name="PersonalData"
        component={Screens.PersonalDataScreen}
        options={stackOptions.PersonalData}
      />
      <Stack.Screen
        name="PersonalDataDetails"
        component={Screens.PersonalDataDetailsScreen}
        options={stackOptions.PersonalDataDetails}
      />
      <Stack.Screen
        name="DeliveryOrderInFlight"
        component={Screens.DeliveryOrderInFlightScreen}
        options={stackOptions.DeliveryOrderInFlight}
      />
      <Stack.Screen
        name="SweetpassUpgrade"
        component={Screens.SweetpassUpgradeScreen}
        options={stackOptions.SweetpassUpgrade}
      />
      <Stack.Screen
        name="OrderProductConfirmProduct"
        component={Screens.OrderProductConfirmProductScreen}
        options={stackOptions.OrderProductConfirmProduct}
      />
      <Stack.Screen
        name="OrderProductConfirmLocation"
        component={Screens.OrderProductConfirmLocationScreen}
        options={stackOptions.OrderProductConfirmLocation}
      />
    </Stack.Navigator>
  );
};

// ==================================================
// AUTH STACK
// ==================================================

export const AuthStackScreen = (
  props: AuthStackScreenProps,
): React.ReactElement => {
  const stackOptions = useAuthStackOptions(props);
  const Stack = AuthStackNavigator;

  return (
    <Stack.Navigator screenOptions={stackOptions.Navigator}>
      <Stack.Screen
        name="Login"
        component={Screens.LoginScreen}
        options={stackOptions.Login}
      />
      <Stack.Screen
        name="Join"
        component={Screens.JoinScreen}
        options={stackOptions.Join}
      />
      <Stack.Screen
        name="JoinVerification"
        component={Screens.JoinVerificationScreen}
        options={stackOptions.JoinVerification}
      />
      <Stack.Screen
        name="ForgotPassword"
        component={Screens.ForgotPasswordScreen}
        options={stackOptions.ForgotPassword}
      />
    </Stack.Navigator>
  );
};

// ==================================================
// ERROR STACK
// ==================================================

export const ErrorStackScreen =
  ({}: ErrorStackScreenProps): React.ReactElement => {
    const stackOptions = useErrorStackOptions();
    const Stack = ErrorStackNavigator;

    return (
      <Stack.Navigator screenOptions={stackOptions.Navigator}>
        <Stack.Screen
          name="NotFound"
          component={Screens.NotFoundScreen}
          options={stackOptions.NotFound}
        />
      </Stack.Navigator>
    );
  };

// ==================================================
// MAINTENANCE STACK
// ==================================================

export const MaintenanceStackScreen = () => {
  const Stack = MaintenanceStackNavigator;
  const stackOptions = useMaintenanceStackOptions();

  return (
    <Stack.Navigator>
      <Stack.Screen
        name="MaintenanceMode"
        component={Screens.MaintenanceModeScreen}
        options={stackOptions.MaintenanceMode}
      />
    </Stack.Navigator>
  );
};
